'use client'
import React, { useState } from 'react'

import Link from 'next/link'
import { usePathname } from 'next/navigation'

import { CMSLink } from '@/components/Link'
import type { EnvironmentConfig } from '@/utilities/getEnvironment'
import type { Header } from '@/payload-types'

import { HeaderNav } from './Nav'
import { MobileNav } from './MobileNav'

interface HeaderClientProps {
  data: Header
  envConfig?: EnvironmentConfig
}

export const HeaderClient: React.FC<HeaderClientProps> = ({ data, envConfig }) => {
  /* Storing the value in a useState to avoid hydration errors */
  const [theme, setTheme] = useState<string | null>(null)
  const pathname = usePathname()

  // Check if we're on the home page (supports both locales)
  const isHomePage = pathname === '/' || pathname === '/en' || pathname === '/uk'

  // Get current locale from pathname
  const locale = pathname.startsWith('/en') ? 'en' : 'uk'
  const homeUrl = `/${locale}`

  // Use environment config passed from server
  const logoColor = envConfig?.showBadge ? envConfig.color : undefined

  return (
    <header
      className="sticky top-0 z-20 bg-background border-b border-border"
      {...(theme ? { 'data-theme': theme } : {})}
    >
      <div className="container py-1 md:py-2 flex justify-between items-center">
        <Link
          href={homeUrl}
          className="text-4xl font-bold"
          style={logoColor ? { color: logoColor } : {}}
        >
          RS-42
        </Link>
        <div className="flex items-center gap-4">
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-4">
            <HeaderNav data={data} />
            {isHomePage && data.ctaButton && data.ctaButton.link && (
              <CMSLink {...data.ctaButton.link} appearance="default">
                {data.ctaButton.text}
              </CMSLink>
            )}
          </div>

          {/* Mobile Navigation */}
          <MobileNav data={data} locale={locale} />
        </div>
      </div>
    </header>
  )
}
